import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { DateTimeDisplay } from '@/components/ui/datetime-display';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { CheckCircle, AlertTriangle } from 'lucide-react';
import { useLocation, useNavigate, Link } from 'react-router';
import { useState } from 'react';

export const CouponRedeemPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [isRedeemed, setIsRedeemed] = useState(false);
  const [isRedeeming, setIsRedeeming] = useState(false);

  // Get voucher ID from location state
  const voucherId = location.state?.voucherId;

  // TODO: Replace with actual API call to fetch voucher details
  const mockVoucher = {
    id: voucherId || '1',
    title: 'Restaurant Sunshine - 20% Rabatt',
    description: 'Genießen Sie 20% Rabatt auf Ihre gesamte Rechnung.',
    provider: {
      name: 'Restaurant Sunshine',
      address: 'Musterstraße 123, 52349 Düren',
      phone: '+49 2421 123456',
      website: 'www.restaurant-sunshine.de',
    },
    validFrom: '2024-01-01T00:00:00Z',
    validUntil: '2024-12-31T23:59:59Z',
    discount: '20%',
    category: 'Gastronomie',
    terms: 'Nicht mit anderen Aktionen kombinierbar. Reservierung empfohlen.',
    redemptionInstructions: 'Zeigen Sie diesen Gutschein beim Bezahlen vor oder nennen Sie den Gutscheincode.',
    code: `SW-2024-${(voucherId || '1').padStart(6, '0')}`,
  };

  const handleRedeem = async () => {
    setIsRedeeming(true);
    try {
      // TODO: Implement API call to redeem voucher
      await new Promise(resolve => setTimeout(resolve, 2000)); // Simulate API call
      setIsRedeemed(true);
    } catch {
      // Error handled silently
    } finally {
      setIsRedeeming(false);
    }
  };

  if (!voucherId) {
    return (
      <div className='container mx-auto px-4 py-8'>
        <div className='max-w-2xl mx-auto'>
          <Alert>
            <AlertTriangle className='h-4 w-4' />
            <AlertDescription>
              Kein Gutschein ausgewählt. Bitte gehen Sie zu "Meine Gutscheine" und wählen Sie einen Gutschein zum
              Einlösen aus.
            </AlertDescription>
          </Alert>
          <div className='mt-4 text-center'>
            <Button asChild>
              <Link to='/meine-gutscheine'>Zu meinen Gutscheinen</Link>
            </Button>
          </div>
        </div>
      </div>
    );
  }

  if (isRedeemed) {
    return (
      <div className='container mx-auto px-4 py-8'>
        <div className='max-w-2xl mx-auto'>
          <Card>
            <CardHeader className='text-center'>
              <CheckCircle className='h-16 w-16 text-green-500 mx-auto mb-4' />
              <CardTitle className='text-2xl text-green-600'>Gutschein erfolgreich eingelöst!</CardTitle>
              <CardDescription>Ihr Gutschein wurde erfolgreich eingelöst und ist nicht mehr gültig.</CardDescription>
            </CardHeader>
            <CardContent className='space-y-4'>
              <div className='text-center'>
                <p className='text-gray-600 mb-4'>Vielen Dank für die Nutzung unseres Gutschein-Services!</p>
                <div className='space-x-4'>
                  <Button asChild variant='outline'>
                    <Link to='/meine-gutscheine'>Zu meinen Gutscheinen</Link>
                  </Button>
                  <Button asChild className='bg-cyan-600 hover:bg-cyan-700'>
                    <Link to='/coupons'>Neue Gutscheine entdecken</Link>
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className='container mx-auto px-4 py-8'>
      <div className='max-w-2xl mx-auto'>
        {/* Back Button */}
        <Button variant='ghost' onClick={() => navigate('/meine-gutscheine')} className='mb-6'>
          ← Zurück zu meinen Gutscheinen
        </Button>

        {/* Voucher Details */}
        <Card className='mb-6'>
          <CardHeader>
            <div className='flex justify-between items-start'>
              <div>
                <CardTitle className='text-xl text-cyan-700'>{mockVoucher.title}</CardTitle>
                <CardDescription className='mt-2'>{mockVoucher.description}</CardDescription>
              </div>
              <Badge variant='secondary' className='bg-lime-100 text-lime-800'>
                {mockVoucher.discount}
              </Badge>
            </div>
          </CardHeader>
          <CardContent className='space-y-4'>
            <div className='grid md:grid-cols-2 gap-4'>
              <div>
                <h4 className='font-semibold text-gray-800'>Anbieter</h4>
                <p className='text-gray-600'>{mockVoucher.provider.name}</p>
                <p className='text-sm text-gray-500'>{mockVoucher.provider.address}</p>
              </div>
              <div>
                <h4 className='font-semibold text-gray-800'>Gültigkeit</h4>
                <p className='text-gray-600'>
                  Bis <DateTimeDisplay date={mockVoucher.validUntil} format='date-only' />
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Redemption Instructions */}
        <Card className='mb-6'>
          <CardHeader>
            <CardTitle>Einlösung</CardTitle>
            <CardDescription>So lösen Sie Ihren Gutschein ein</CardDescription>
          </CardHeader>
          <CardContent className='space-y-4'>
            <Alert>
              <AlertTriangle className='h-4 w-4' />
              <AlertDescription>
                <strong>Wichtig:</strong> Nach der Einlösung ist dieser Gutschein nicht mehr gültig und kann nicht
                erneut verwendet werden.
              </AlertDescription>
            </Alert>

            <div>
              <h4 className='font-semibold text-gray-800 mb-2'>Anweisungen:</h4>
              <p className='text-gray-600'>{mockVoucher.redemptionInstructions}</p>
            </div>

            <div>
              <h4 className='font-semibold text-gray-800 mb-2'>Gutscheincode:</h4>
              <div className='bg-gray-100 p-3 rounded-lg border'>
                <code className='text-lg font-mono text-cyan-700'>{mockVoucher.code}</code>
              </div>
            </div>

            <div>
              <h4 className='font-semibold text-gray-800 mb-2'>Bedingungen:</h4>
              <p className='text-sm text-gray-600'>{mockVoucher.terms}</p>
            </div>
          </CardContent>
        </Card>

        {/* Redeem Button */}
        <Card>
          <CardContent className='pt-6'>
            <div className='text-center space-y-4'>
              <p className='text-gray-600'>Sind Sie bereit, diesen Gutschein einzulösen?</p>
              <Button
                onClick={handleRedeem}
                disabled={isRedeeming}
                className='bg-green-600 hover:bg-green-700 w-full'
                size='lg'
              >
                {isRedeeming ? 'Wird eingelöst...' : 'Gutschein jetzt einlösen'}
              </Button>
              <p className='text-xs text-gray-500'>
                Durch Klicken auf "Gutschein jetzt einlösen" bestätigen Sie, dass Sie den Gutschein verwenden möchten.
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};
